import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for intersection observer with performance optimizations
 * Respects prefers-reduced-motion for accessibility
 * 
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Root margin for early/late triggering
 * @param {boolean} options.triggerOnce - Whether to disconnect after first trigger
 * @returns {[React.Ref, boolean]} - [ref to attach, inView state]
 */
export const useInView = ({ 
  threshold = 0.1, 
  rootMargin = '0px 0px -10% 0px',
  triggerOnce = true 
} = {}) => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check for prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // If reduced motion is preferred, immediately set inView to true
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;
        
        if (isIntersecting) {
          setInView(true);
          
          // Disconnect observer if triggerOnce is true
          if (triggerOnce && observerRef.current) {
            observerRef.current.disconnect();
          }
        } else if (!triggerOnce) {
          setInView(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    // Start observing
    observerRef.current.observe(element);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, inView];
};